import { useState, useEffect, useMemo } from 'react';
import { Search, Filter, Hotel, FileText, Calendar, Mail } from 'lucide-react';
import { motion } from 'framer-motion';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { tourFileAPI, seriesTemplateAPI } from '../services/api';
import { SendToHotelModal } from './SendToHotelModal';

interface BookingsPageProps {
  onSelectBooking: (id: string) => void;
}

export function BookingsPage({ onSelectBooking }: BookingsPageProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedHotel, setSelectedHotel] = useState<string>('all');
  const [selectedMode, setSelectedMode] = useState<'hotels' | 'vouchers' | null>(null);
  const [selectedBookingGroup, setSelectedBookingGroup] = useState<string>('');
  const [tourFiles, setTourFiles] = useState<any[]>([]);
  const [seriesTemplates, setSeriesTemplates] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showSendModal, setShowSendModal] = useState(false);
  const [selectedTourFile, setSelectedTourFile] = useState<any>(null);

  // Fetch tour files and series templates
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [toursResponse, templatesResponse] = await Promise.all([
          tourFileAPI.getAll(),
          seriesTemplateAPI.getAll()
        ]);
        
        if (toursResponse.success) {
          setTourFiles(toursResponse.data);
        }
        
        if (templatesResponse.success) {
          setSeriesTemplates(templatesResponse.data);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Extract all unique hotels from tour files
  const allHotels = useMemo(() => {
    const hotelSet = new Set<string>();
    tourFiles.forEach(tour => {
      try {
        const itinerary = typeof tour.itinerary === 'string' ? JSON.parse(tour.itinerary) : (tour.itinerary || []);
        itinerary.forEach((row: any) => {
          if (row.hotelName && row.hotelName.trim()) {
            hotelSet.add(row.hotelName.trim());
          }
        });
      } catch (e) {
        // Ignore parse errors
      }
    });
    return Array.from(hotelSet).sort();
  }, [tourFiles]);

  // Filter bookings by selected hotel
  const filteredBookings = useMemo(() => {
    return tourFiles.filter(tour => {
      // Search filter
      const matchesSearch = !searchQuery || 
        tour.tourName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        tour.fileCode?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        tour.clientName?.toLowerCase().includes(searchQuery.toLowerCase());
      
      if (!matchesSearch) return false;

      // Hotel filter
      if (selectedHotel === 'all') return true;

      try {
        const itinerary = typeof tour.itinerary === 'string' ? JSON.parse(tour.itinerary) : (tour.itinerary || []);
        return itinerary.some((row: any) => row.hotelName?.trim() === selectedHotel);
      } catch (e) {
        return false;
      }
    });
  }, [tourFiles, searchQuery, selectedHotel]);

  // Get hotel bookings data for selected hotel
  const hotelBookingsData = useMemo(() => {
    if (selectedHotel === 'all') return [];

    return filteredBookings.map(tour => {
      try {
        const itinerary = typeof tour.itinerary === 'string' ? JSON.parse(tour.itinerary) : (tour.itinerary || []);
        const hotelRows = itinerary.filter((row: any) => row.hotelName?.trim() === selectedHotel);
        
        // Calculate check-in and check-out dates
        const checkInDate = hotelRows.length > 0 ? hotelRows[0].date : '';
        let checkOutDate = hotelRows.length > 0 ? hotelRows[hotelRows.length - 1].date : '';
        
        // Add 1 day to check-out date
        if (checkOutDate) {
          const parts = checkOutDate.trim().split('-');
          if (parts.length === 3) {
            const day = parseInt(parts[0]);
            const monthStr = parts[1];
            const year = parseInt('20' + parts[2]);
            
            const monthMap: {[key: string]: number} = {
              'Jan': 0, 'Feb': 1, 'Mar': 2, 'Apr': 3,
              'May': 4, 'Jun': 5, 'Jul': 6, 'Aug': 7,
              'Sep': 8, 'Oct': 9, 'Nov': 10, 'Dec': 11
            };
            
            const month = monthMap[monthStr];
            if (month !== undefined) {
              const date = new Date(year, month, day);
              date.setDate(date.getDate() + 1); // Add 1 day
              
              const newDay = String(date.getDate()).padStart(2, '0');
              const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
              const newMonth = monthNames[date.getMonth()];
              const newYear = String(date.getFullYear()).slice(-2);
              checkOutDate = `${newDay}-${newMonth}-${newYear}`;
            }
          }
        }
        
        const roomNights = hotelRows.length;
        
        // Generate Group Name: SeriesCode + ArrivalDate (e.g., DIH15Feb26)
        let groupName = tour.fileCode || '';
        if (tour.tourType === 'SERIES' && tour.startDate && tour.tourName) {
          // Find the series template by matching tour name
          const template = seriesTemplates.find(t => 
            t.seriesName.toLowerCase() === tour.tourName.toLowerCase()
          );
          
          if (template && template.seriesCode) {
            // Use series code from template
            const seriesCode = template.seriesCode;
            
            // Format arrival date as ddMmmYY (e.g., 15Feb26)
            const startDate = new Date(tour.startDate);
            const day = String(startDate.getDate()).padStart(2, '0');
            const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
            const month = monthNames[startDate.getMonth()];
            const year = String(startDate.getFullYear()).slice(-2);
            const formattedDate = `${day}${month}${year}`;
            
            groupName = `${seriesCode}${formattedDate}`;
          }
        }
        
        return {
          groupName: groupName,
          fileName: tour.tourName,
          checkIn: checkInDate,
          checkOut: checkOutDate,
          roomNights: roomNights,
          rooms: 12, // Fixed value of 12 rooms
          status: '', // Empty status
          tourFile: tour // Full tour file data for voucher mode
        };
      } catch (e) {
        return null;
      }
    }).filter(Boolean);
  }, [filteredBookings, selectedHotel, seriesTemplates]);

  const getfileStatusColor = (fileStatus: string) => {
    switch (fileStatus) {
      case 'completed': return 'bg-green-100 text-green-700 border-green-300';
      case 'in-progress': return 'bg-amber-100 text-amber-700 border-amber-300';
      case 'upcoming': return 'bg-blue-100 text-blue-700 border-blue-300';
      default: return 'bg-gray-100 text-gray-700 border-gray-300';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading bookings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Bookings</h1>
          <p className="text-gray-600 mt-1">{filteredBookings.length} bookings found</p>
        </div>
      </div>

      {/* Mode Selection - Hotels or Vouchers */}
      {!selectedMode ? (
        <div className="grid grid-cols-2 gap-6 max-w-2xl mx-auto mt-12">
          <motion.div
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setSelectedMode('hotels')}
            className="p-8 bg-white rounded-lg shadow-md border-2 border-gray-200 hover:border-violet-500 cursor-pointer transition-all"
          >
            <div className="text-center">
              <Hotel className="w-16 h-16 text-violet-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Hotels</h3>
              <p className="text-gray-500">View and manage hotel bookings</p>
            </div>
          </motion.div>

          <motion.div
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setSelectedMode('vouchers')}
            className="p-8 bg-white rounded-lg shadow-md border-2 border-gray-200 hover:border-violet-500 cursor-pointer transition-all"
          >
            <div className="text-center">
              <FileText className="w-16 h-16 text-violet-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Vouchers</h3>
              <p className="text-gray-500">View and manage vouchers</p>
            </div>
          </motion.div>
        </div>
      ) : (
        <>
          {/* Back Button */}
          <Button 
            onClick={() => { setSelectedMode(null); setSelectedHotel('all'); }} 
            variant="outline"
            className="mb-4"
          >
            ← Back to Options
          </Button>

          {/* Search and Filters */}
          <div className="flex gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                type="text"
                placeholder="Search by file code, tour name, customer name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="w-64">
              <Select value={selectedHotel} onValueChange={setSelectedHotel}>
                <SelectTrigger>
                  <SelectValue placeholder={`Select ${selectedMode === 'hotels' ? 'hotel' : 'voucher'}`} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">
                    <div className="flex items-center gap-2">
                      {selectedMode === 'hotels' ? <Hotel className="w-4 h-4" /> : <FileText className="w-4 h-4" />}
                      All {selectedMode === 'hotels' ? 'Hotels' : 'Vouchers'}
                    </div>
                  </SelectItem>
                  {allHotels.map(hotel => (
                    <SelectItem key={hotel} value={hotel}>{hotel}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Bookings Table */}
          {selectedHotel !== 'all' ? (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 bg-violet-50 border-b border-gray-200 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {selectedMode === 'hotels' ? <Hotel className="w-5 h-5 text-violet-600" /> : <FileText className="w-5 h-5 text-violet-600" />}
                  <h2 className="text-lg font-semibold text-gray-900">
                    {selectedMode === 'hotels' ? 'Hotel' : 'Voucher'}: {selectedHotel}
                  </h2>
                </div>
                {selectedMode === 'hotels' && (
                  <Button
                    onClick={() => {
                      setSelectedBookingGroup('');
                      setShowSendModal(true);
                    }}
                    className="bg-violet-600 hover:bg-violet-700 text-white flex items-center gap-2"
                    size="sm"
                  >
                    <Mail className="w-4 h-4" />
                    Send to Hotel
                  </Button>
                )}
              </div>
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Group Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">File Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Check-in</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Check-out</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Room Nights</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rooms</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {hotelBookingsData.map((booking: any, idx: number) => (
                    <motion.tr
                      key={idx}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        {selectedMode === 'vouchers' ? (
                          <span 
                            className="font-medium text-violet-600 cursor-pointer hover:text-violet-800 hover:underline"
                            onClick={() => {
                              setSelectedBookingGroup(booking.groupName);
                              setSelectedTourFile(booking.tourFile || null);
                              setShowSendModal(true);
                            }}
                          >
                            {booking.groupName}
                          </span>
                        ) : (
                          <span className="font-medium text-violet-600">
                            {booking.groupName}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium">{booking.fileName}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2 text-sm">
                          <Calendar className="w-4 h-4 text-gray-400" />
                          {booking.checkIn}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2 text-sm">
                          <Calendar className="w-4 h-4 text-gray-400" />
                          {booking.checkOut}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="font-semibold text-blue-600">{booking.roomNights}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="font-semibold">{booking.rooms}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {booking.status && (
                          <Badge className="bg-blue-100 text-blue-700 border border-blue-300">
                            {booking.status}
                          </Badge>
                        )}
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
              {selectedMode === 'hotels' ? <Hotel className="w-16 h-16 text-gray-300 mx-auto mb-4" /> : <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />}
              <h3 className="text-lg font-medium text-gray-900 mb-2">Select a {selectedMode === 'hotels' ? 'Hotel' : 'Voucher'}</h3>
              <p className="text-gray-500">Choose a {selectedMode === 'hotels' ? 'hotel' : 'voucher'} from the dropdown above to view bookings</p>
            </div>
          )}
        </>
      )}

      {/* Send to Hotel/Voucher Modal */}
      <SendToHotelModal
        isOpen={showSendModal}
        onClose={() => {
          setShowSendModal(false);
          setSelectedBookingGroup('');
          setSelectedTourFile(null);
        }}
        hotelName={selectedHotel}
        bookings={hotelBookingsData.map((b: any) => ({
          groupName: b.groupName,
          fileName: b.fileName,
          checkIn: b.checkIn,
          checkOut: b.checkOut,
          nights: b.roomNights,
          rooms: b.rooms
        }))}
        mode={selectedMode || 'hotels'}
        groupName={selectedBookingGroup}
        tourFileData={selectedTourFile}
      />
    </div>
  );
}
